import { BCMSRecord, BCMSStateManagerCRUD, DisabledPrompt, Link, StateManager, useBCMSContext, useStateManager } from "@bcm-dev/bcms-vite";
import { TableBody, TableColumnContainer, TableColumnView, TableItemContainer, TableItemTextView, TableItemView, TableView, WidgetContainer, WidgetView } from "components/shared";
import { PrimaryButton, UICheckbox } from "components/ui";
import Classes, { PassThruHistory } from "model";
import React from "react";
import { SVGAsset } from "res";
import MobileHistoryACHPassThruTable from "../MobileHistoryACHPassThruTable";
import NoFilesToDisplayTableRow from "../NoFilesToDisplayTableRow";
import "./styles.css";
import "./mobile.css";


/**
 * @function HistoryACHPassThruWidget
 * @returns {React.Component}
 * @exported true
 * @description This is the widget component that shows the ACH Pass-Thru History on the PassThruACHPage.
 * @example
 * <PassThruACHPage>
 *   <PendingACHPassThruWidget />
 *   <HistoryACHPassThruWidget />
 * </PassThruACHPage>
 */
export default function HistoryACHPassThruWidget(props) {
  const globalController = useBCMSContext();
  BCMSRecord.globalControllerFor(Classes, globalController);

  const data = { ...getDefaultData(), ...props.data ?? {} };
  const ui = { ...getDefaultUI(), ...props.ui ?? {} };

  const controller = (state, setState) => ({
      data: state.data,
      ui: state.ui,

      // Toggles the template form. "show" is a boolean value of `true` or `false`.
      onChange: prop => value => {
        BCMSStateManagerCRUD.init(state)
              .update([ "data", prop ], value)
              .submit(setState);
      },

      // Checks or unchecks a history row.
      onRowChecked: id => value => {
        // Updated the "isChecked" property.
        data.history = data.history.reduce((acc, history) => {
          if(history.id === id) { history.isChecked = value; }
          acc.push(history);
          return acc;
        }, []);
        BCMSStateManagerCRUD.init(state)
              .update([ "data", "history" ], data.history)
              .submit(setState);
      },

      onSelectAllCheckmarksChange: value => {
        data.history = data.history.reduce((acc, history) => {
          history.isChecked = value;
          acc.push(history);
          return acc;
        }, []);
        BCMSStateManagerCRUD.init(state)
              .update([ "data", "history" ], data.history)
              .submit(setState);
      }
  });

  return (
    <StateManager state={ { data, ui } } controller={ controller }>
      <WidgetContent/>
    </StateManager>
  );
}

export const getDefaultData = () => ({
  history: PassThruHistory.initAll()
});
export const getDefaultUI = () => ({
  // no ui for now.
});

function WidgetContent(props) {
  const { data, ui, onSelectAllCheckmarksChange } = useStateManager();
  const hasHistory = data.history.length > 0;
  const atLeastOneRowIsChecked = data.history.some(history => history.isChecked === true);
  const allRowsAreChecked = data.history.every(history => history.isChecked === true);
  const actions = { onSelectAllCheckmarksChange, allRowsAreChecked};

  const historyRows = data.history.reduce((acc, history) => {
    acc.push(<HistoryACHTableRow key={ history.id } history={ history } />);
    return acc;
  }, []);

  return (
    <WidgetContainer className="history-ach-pass-thru-widget">
      <WidgetView text="ACH Pass-Thru History" dataPoint="History_Title">
        <div className="show-on-desktop">
          <TableView className="history-ach-pass-thru-table">
            <TableColumnContainer>
              <TableColumnView text={<UICheckbox label="" value={allRowsAreChecked ? "checked" : ""} onChange={onSelectAllCheckmarksChange} />} />
              <TableColumnView
                text={
                  <>
                    <span className="created-by-label">Created By</span>
                    <DisabledPrompt>
                      <SVGAsset id="SortIcon" className="clickable" />
                    </DisabledPrompt>
                  </>
                }
              />
              <TableColumnView
                text={
                  <>
                    <span className="created-label">Created</span>
                    <DisabledPrompt>
                      <SVGAsset id="SortIcon" className="clickable" />
                    </DisabledPrompt>
                  </>
                }
              />
              <TableColumnView
                text={
                  <>
                    <span className="updated-label">Updated</span>
                    <DisabledPrompt>
                      <SVGAsset id="ArrowDownIcon" className="clickable" />
                    </DisabledPrompt>
                  </>
                }
              />
              <TableColumnView
                className="table-large-column"
                text={
                  <>
                    <span className="status-label">Status</span>
                    <DisabledPrompt>
                      <SVGAsset id="SortIcon" className="clickable" />
                    </DisabledPrompt>
                  </>
                }
              />
              <TableColumnView
                className="table-large-column"
                text={
                  <>
                    <span className="file-name-label">File Name</span>
                    <DisabledPrompt>
                      <SVGAsset id="SortIcon" className="clickable" />
                    </DisabledPrompt>
                  </>
                }
              />
            </TableColumnContainer>
            <TableBody>
              {hasHistory && historyRows}
              {!hasHistory && <NoFilesToDisplayTableRow colSpan="6" />}
            </TableBody>
          </TableView>
        </div>
        
        <div className="show-on-mobile">
          <MobileHistoryACHPassThruTable data={data} actions={actions} />
        </div>
        <DisabledPrompt>
          <PrimaryButton text="Download"  dataPoint="Download_Button"/>
        </DisabledPrompt>
      </WidgetView>
    </WidgetContainer>
  );
}

/**
 * @function HistoryACHTableRows
 * @return {React.Component}
 * @exported true
 * @description The table row that shows data in the History ACH Table
 */

function HistoryACHTableRow(props) {
  const { onRowChecked } = useStateManager();
  const history = props.history ?? {};

  return(
    <TableItemContainer>
        <TableItemView>
          <UICheckbox label="" value={ history.isChecked } onChange={ onRowChecked(history.id) } dataPoint={`History_File_${history.id}`} />
        </TableItemView>
        <TableItemTextView text={ history.createdBy } />
        <TableItemTextView text={ history.createdDate.format() } />
        <TableItemTextView text={ history.updatedDate.format() } />
        <TableItemTextView text={ history.status }  dataPoint={`History_Status_${history.id}`}/>
        <TableItemView>
          <DisabledPrompt>
            <Link>
              <p>{ history.fileName }</p>
            </Link>
          </DisabledPrompt>
        </TableItemView>
    </TableItemContainer>
  );
}